import React, { useState, useEffect, useRef } from 'react';
import { formatRuntime } from '../../utils/movieUtils';
import { useTMDBMovie, useStreamingProviders } from '../../hooks/useTMDBData';
import { buildPosterSrcSet, tmdbIdOf } from '../../services/tmdbService';
import { Play, ExternalLink, Users, Film } from 'lucide-react';
import { useEmailAuth } from '../../hooks/useEmailAuth';
import NameField from '../common/NameField';

const ViewerModal = ({ 
  scheduledMovie, 
  isVisible, 
  onClose, 
  isAdmin = false,
  movieStatus = 'coming',
  onStatusChange,
  viewers = [],
  userName = '',
  onJoinWatching,
  onLeaveWatching,
  onToggleReady,
  countdown = null,
  onStartCountdown,
  onCreateChannel,
  onLookupChannel,
  onSendReviewPrompt,
  streamingServices = [],
  reviews = [],
  onOpenReviewModal,
  getFreddyMessage = () => "",
  isSurveyMode = false
}) => {
  const [joinName, setJoinName] = useState('');
  const [showMovieDetails, setShowMovieDetails] = useState(false);

  // The movie's chat channel, looked up whenever the modal opens.
  //   checking - lookup in flight
  //   found    - show the link, to everyone
  //   absent   - no channel yet; admins get the button to open one
  //   creating - admin's request in flight
  //   error    - creation failed, with a reason worth showing
  const [channel, setChannel] = useState({ status: 'checking', url: '', note: '' });

  // NameField fills joinName in from the signed-in profile; the modal only has
  // to hold whatever name it settles on.
  const { getRememberedName } = useEmailAuth();


  // Fetch TMDB data for this movie (skip for placeholder movies)
  const movieData = scheduledMovie?.movie || scheduledMovie;
  const { tmdbData, loading: tmdbLoading } = useTMDBMovie(
    movieData?.isPlaceholder ? null : movieData?.title,
    movieData?.isPlaceholder ? null : movieData?.year,
    movieData?.isPlaceholder ? null : tmdbIdOf(movieData)
  );
  const { providers } = useStreamingProviders(tmdbData?.id);

  // App rebuilds its handlers on every render, and it re-renders once a second
  // during a countdown. Depending on the prop directly would therefore fire a
  // Discord lookup per tick, so the effect below keys only on the movie.
  const lookupRef = useRef(onLookupChannel);
  lookupRef.current = onLookupChannel;

  useEffect(() => {
    if (!isVisible || !movieData?.id || !lookupRef.current) {
      setChannel({ status: 'absent', url: '', note: '' });
      return undefined;
    }

    let cancelled = false;
    setChannel({ status: 'checking', url: '', note: '' });

    Promise.resolve(lookupRef.current(movieData.id)).then((res) => {
      // The modal may have moved to another movie while this was in flight.
      if (cancelled) return;
      setChannel(res?.channelUrl
        ? { status: 'found', url: res.channelUrl, note: '' }
        : { status: 'absent', url: '', note: '' });
    });

    return () => { cancelled = true; };
  }, [isVisible, movieData?.id]);

  // Unlike the channel, which either exists or does not, a review nudge is a
  // repeatable action - a second one is a legitimate reminder, not a mistake.
  // So this reports the last send and stays clickable.
  const [reviewPrompt, setReviewPrompt] = useState({ busy: false, note: '', failed: false });

  useEffect(() => {
    setReviewPrompt({ busy: false, note: '', failed: false });
  }, [movieData?.id]);

  const handleSendReviewPrompt = async () => {
    if (!onSendReviewPrompt || !movieData?.id) return;

    setReviewPrompt({ busy: true, note: '', failed: false });
    const res = await onSendReviewPrompt(movieData.id);

    setReviewPrompt(res?.sent
      ? { busy: false, failed: false, note: 'Review request sent' }
      : { busy: false, failed: true, note: res?.reason || 'could not reach chat' });
  };

  const handleCreateChannel = async () => {
    if (!onCreateChannel || !movieData?.id) return;

    setChannel({ status: 'creating', url: '', note: '' });
    const res = await onCreateChannel(movieData.id);

    setChannel(res?.sent
      ? {
          status: 'found',
          url: res.channelUrl || '',
          note: res.channelCreated ? 'Channel created' : 'Channel already existed'
        }
      : {
          status: 'error',
          url: '',
          note: res?.reason || 'could not reach chat'
        });
  };

  const movieStatusOptions = [
    { id: 'watched', label: 'Already watched', color: 'bg-gray-600' },
    { id: 'coming', label: 'Coming soon', color: 'bg-blue-600' },
    { id: 'upnext', label: 'Up next', color: 'bg-yellow-600' },
    { id: 'ready', label: 'Getting ready', color: 'bg-orange-600' },
    { id: 'playing', label: 'Now playing', color: 'bg-red-600' }
  ];

  if (!isVisible || !scheduledMovie) return null;

  const getSelectedUserName = () => joinName.trim();

  // Left empty for NameField to refill from the profile, so joining a second
  // session does not ask who you are all over again.
  const resetNameInputs = () => setJoinName('');

  const isUserWatching = (name) => {
    if (!name) return false;
    return viewers.some(viewer => viewer.name === name);
  };

  const canStartWatching = () => {
    return movieStatus === 'playing' || movieStatus === 'upnext' || movieStatus === 'ready';
  };

  const currentStatusOption = movieStatusOptions.find(opt => opt.id === movieStatus);
  const readyCount = viewers.filter(v => v.ready).length;
  const getStreamingServiceById = (id) => {
    // Default streaming services (comprehensive list)
    const defaultServices = [
      { id: 'netflix', name: 'Netflix', color: 'bg-red-600', icon: '🎬' },
      { id: 'amazon', name: 'Amazon Prime', color: 'bg-blue-600', icon: '📺' },
      { id: 'disney', name: 'Disney+', color: 'bg-blue-800', icon: '🏰' },
      { id: 'hulu', name: 'Hulu', color: 'bg-green-600', icon: '🎭' },
      { id: 'hbo', name: 'HBO Max', color: 'bg-purple-700', icon: '👑' },
      { id: 'apple', name: 'Apple TV+', color: 'bg-gray-800', icon: '🍎' },
      { id: 'paramount', name: 'Paramount+', color: 'bg-blue-500', icon: '⭐' },
      { id: 'peacock', name: 'Peacock', color: 'bg-purple-600', icon: '🦚' },
      { id: 'showtime', name: 'Showtime', color: 'bg-red-800', icon: '🎪' },
      { id: 'starz', name: 'Starz', color: 'bg-black', icon: '✨' },
      { id: 'free', name: 'Free (Tubi/Crackle)', color: 'bg-green-700', icon: '🆓' },
      { id: 'rent', name: 'Digital Rental', color: 'bg-orange-700', icon: '💰' },
      { id: 'physical', name: 'Physical Media', color: 'bg-gray-700', icon: '💿' },
      { id: 'theater', name: 'In Theaters', color: 'bg-yellow-600', icon: '🎫' },
      // TMDB services (with logos)
      { id: 'tmdb_netflix', name: 'Netflix', logo: 'https://image.tmdb.org/t/p/original/pbpMk2JmcoNnQwx5JGpXngfoWtp.jpg', color: 'bg-red-600' },
      { id: 'tmdb_amazon_prime', name: 'Amazon Prime Video', logo: 'https://image.tmdb.org/t/p/original/68MNrwlkpF7WnmNPXLah69CR5cb.jpg', color: 'bg-blue-600' },
      { id: 'tmdb_shudder', name: 'Shudder', logo: 'https://image.tmdb.org/t/p/original/5VISkzLZOA1UpzAdVlRPtBXpjI.jpg', color: 'bg-red-900' },
      { id: 'tmdb_disney_plus', name: 'Disney Plus', logo: 'https://image.tmdb.org/t/p/original/7rwgEs15tFwyR9NPQ5vpzxTj19Q.jpg', color: 'bg-blue-800' },
      { id: 'tmdb_hbo_max', name: 'HBO Max', logo: 'https://image.tmdb.org/t/p/original/Ajqyt5aNxNGjmF9uOfxArGrdf3X.jpg', color: 'bg-purple-700' },
      { id: 'tmdb_max', name: 'Max', logo: 'https://image.tmdb.org/t/p/original/Ajqyt5aNxNGjmF9uOfxArGrdf3X.jpg', color: 'bg-purple-700' },
      { id: 'tmdb_peacock', name: 'Peacock', logo: 'https://image.tmdb.org/t/p/original/xTVM8uXT9QocigQ08cLMNECZcQB.jpg', color: 'bg-purple-600' },
      { id: 'tmdb_paramount_plus', name: 'Paramount+', logo: 'https://image.tmdb.org/t/p/original/h5DcR0J2EESLitnhR8xLG1QymTE.jpg', color: 'bg-blue-500' },
      { id: 'tmdb_kanopy', name: 'Kanopy', logo: 'https://image.tmdb.org/t/p/original/sVBEF7q7LqjHAWSnKwDbzmr2EMY.jpg', color: 'bg-yellow-600' },
      { id: 'tmdb_criterion', name: 'Criterion Channel', logo: 'https://image.tmdb.org/t/p/original/da3jxo6RdnZZweAgdx4R9YNqHFj.jpg', color: 'bg-black' },
      { id: 'tmdb_amazon_video', name: 'Amazon Video', logo: 'https://image.tmdb.org/t/p/original/68MNrwlkpF7WnmNPXLah69CR5cb.jpg', color: 'bg-orange-600' },
      { id: 'tmdb_hulu', name: 'Hulu', logo: 'https://image.tmdb.org/t/p/original/pqUTCoBcQAGyM93Z515dLovOAKe.jpg', color: 'bg-green-600' },
      { id: 'tmdb_apple_tv', name: 'Apple TV+', logo: 'https://image.tmdb.org/t/p/original/2E03IAZsX4ZaUqM7tXlctEPMGWS.jpg', color: 'bg-gray-800' },
      { id: 'tmdb_showtime', name: 'Showtime', logo: 'https://image.tmdb.org/t/p/original/SvvG9ZIdrjkqLnkTLDelgPEXvP.jpg', color: 'bg-red-800' },
      { id: 'tmdb_starz', name: 'Starz', logo: 'https://image.tmdb.org/t/p/original/5L8VQcCEDC9bD7gpuMWKJnTRz9P.jpg', color: 'bg-black' },
      // Additional TMDB services
      { id: 'tmdb_apple_itunes', name: 'Apple iTunes', icon: '🍎', color: 'bg-gray-700' },
      { id: 'tmdb_google_play', name: 'Google Play Movies', icon: '🎞️', color: 'bg-blue-500' },
      { id: 'tmdb_microsoft', name: 'Microsoft Store', icon: '🪟', color: 'bg-blue-600' },
      { id: 'tmdb_vudu', name: 'Vudu', icon: '🎬', color: 'bg-blue-700' },
      { id: 'tmdb_tubi', name: 'Tubi TV', icon: '🆓', color: 'bg-green-700' },
      { id: 'tmdb_crackle', name: 'Crackle', icon: '🆓', color: 'bg-orange-700' },
      { id: 'tmdb_pluto', name: 'Pluto TV', icon: '🪐', color: 'bg-purple-500' },
      { id: 'tmdb_hbo_now', name: 'HBO Now', icon: '👑', color: 'bg-purple-800' },
      { id: 'tmdb_discovery', name: 'Discovery+', icon: '🔍', color: 'bg-blue-400' },
      { id: 'tmdb_amazon_prime_uk', name: 'Amazon Prime Video (UK)', icon: '📺', color: 'bg-blue-600' },
      { id: 'tmdb_netflix_kids', name: 'Netflix Kids', icon: '👶', color: 'bg-red-400' },
      { id: 'tmdb_youtube_tv', name: 'YouTube TV', icon: '📺', color: 'bg-red-500' },
      { id: 'tmdb_fubotv', name: 'fuboTV', icon: '⚽', color: 'bg-green-500' },
      { id: 'tmdb_epix', name: 'Epix', icon: '🎬', color: 'bg-yellow-600' },
      { id: 'tmdb_netflix_basic', name: 'Netflix basic with Ads', icon: '🎬', color: 'bg-red-500' }
    ];
    
    return defaultServices.find(service => service.id === id) || null;
  };

  const getAverageRatings = () => {
    if (reviews.length === 0) return { overall: 0, scariness: 0 };

    const totalOverall = reviews.reduce((sum, review) => sum + review.overallRating, 0);
    const totalScariness = reviews.reduce((sum, review) => sum + review.scarinessRating, 0);

    return {
      overall: Math.round((totalOverall / reviews.length) * 10) / 10,
      scariness: Math.round((totalScariness / reviews.length) * 10) / 10
    };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100">
              {isSurveyMode ? '🎬 Movie Info' : '🎬 Movie Viewing Session'}
            </h2>
            <button
              onClick={onClose}
              className="bg-red-600 hover:bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {/* Movie Info */}
          <div className="flex items-center gap-4 mb-6">
            {/* Movie Poster or Color Block */}
            {tmdbData?.posterUrl ? (
              <div className="relative">
                <img
                  src={tmdbData.posterUrl}
                  srcSet={buildPosterSrcSet(tmdbData.posterUrl, [92, 154, 185, 342])}
                  sizes="80px"
                  alt={`${movieData.title} poster`}
                  className="w-20 h-30 object-cover rounded border-2 border-orange-600"
                  decoding="async"
                />
                {/* TMDB Rating Badge */}
                {!isSurveyMode && tmdbData.rating && (
                  <div className="absolute -top-2 -right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded-full">
                    ⭐ {tmdbData.rating.toFixed(1)}
                  </div>
                )}
              </div>
            ) : (
              <div className={`w-20 h-30 ${movieData.color} rounded border-2 border-orange-600 flex items-center justify-center`}>
                <span className="text-white text-sm font-bold px-1 leading-tight text-center">
                  {movieData.title.split(' ').map(word => word[0]).join('').slice(0, 3)}
                </span>
              </div>
            )}
            
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-orange-100">{movieData.title}</h3>
                {/* Trailer Button */}
                {tmdbData?.trailerUrl && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(tmdbData.trailerUrl, '_blank');
                    }}
                    className="bg-red-600 hover:bg-red-500 p-1 rounded-full transition-colors"
                    title="Watch Trailer"
                  >
                    <Play size={12} fill="white" />
                  </button>
                )}
              </div>
              
              <p className="text-orange-300 mb-2">
                ({tmdbData?.releaseDate ? new Date(tmdbData.releaseDate).getFullYear() : movieData.year}) • 
                {formatRuntime(tmdbData?.runtime || movieData.runtime)}
                {tmdbData?.genres && tmdbData.genres.length > 0 && (
                  <span> • {tmdbData.genres[0]}</span>
                )}
              </p>
              
              {/* Director and Stars */}
              {tmdbData && (tmdbData.director || tmdbData.stars?.length > 0) && (
                <div className="text-sm text-gray-300 mb-2">
                  {tmdbData.director && (
                    <div className="flex items-center gap-1">
                      <Users size={12} />
                      <span><strong>Director:</strong> {tmdbData.director}</span>
                    </div>
                  )}
                  {tmdbData.stars?.length > 0 && (
                    <div className="flex items-center gap-1">
                      <Film size={12} />
                      <span><strong>Stars:</strong> {tmdbData.stars.slice(0, 2).join(', ')}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Movie Status */}
              {!isSurveyMode && currentStatusOption && (
                <div className={`inline-flex items-center gap-2 px-2 py-1 rounded text-sm text-white ${currentStatusOption.color}`}>
                  <span>{currentStatusOption.label}</span>
                </div>
              )}
            </div>
          </div>

          {/* Movie Details Toggle */}
          {!isSurveyMode && (
            <div className="mb-6">
              <button
                onClick={() => setShowMovieDetails(!showMovieDetails)}
                className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-md transition-colors flex items-center gap-2"
              >
                <Film size={16} />
                {showMovieDetails ? 'Hide Movie Details' : 'Show Movie Details'}
              </button>
            </div>
          )}

          {/* Expanded Movie Details */}
          {(isSurveyMode || showMovieDetails) && (
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <h4 className="text-orange-300 font-bold mb-3">📽️ Movie Details</h4>
              
              {tmdbLoading && (
                <div className="text-gray-400 italic mb-3">Loading detailed movie information...</div>
              )}
              
              {/* Plot Summary */}
              {(tmdbData?.plot || movieData?.plot) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">Plot Summary:</h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{tmdbData?.plot || movieData?.plot}</p>
                </div>
              )}
              
              {/* Cast & Crew */}
              {((tmdbData && (tmdbData.director || tmdbData.writers?.length > 0 || tmdbData.stars?.length > 0)) || movieData?.director) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">Cast & Crew:</h5>
                  <div className="space-y-1 text-sm">
                    {(tmdbData?.director || movieData?.director) && (
                      <div><strong className="text-orange-300">Director:</strong> {tmdbData?.director || movieData?.director}</div>
                    )}
                    {tmdbData?.writers?.length > 0 && (
                      <div><strong className="text-orange-300">Writers:</strong> {tmdbData.writers.slice(0, 3).join(', ')}</div>
                    )}
                    {tmdbData?.stars?.length > 0 && (
                      <div><strong className="text-orange-300">Stars:</strong> {tmdbData.stars.slice(0, 5).join(', ')}</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Streaming Providers */}
              {!isSurveyMode && providers && (providers.flatrate || providers.buy || providers.rent) && (
                <div className="mb-4">
                  <h5 className="text-orange-200 font-semibold mb-2">Where to Watch (US):</h5>
                  <div className="space-y-2">
                    {providers.flatrate && (
                      <div>
                        <span className="text-green-400 font-medium">Stream: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {providers.flatrate.map((provider) => (
                            <span
                              key={provider.provider_id}
                              className="bg-green-600 text-white text-xs px-2 py-1 rounded-full"
                            >
                              {provider.provider_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {providers.buy && (
                      <div>
                        <span className="text-blue-400 font-medium">Buy: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {providers.buy.map((provider) => (
                            <span
                              key={provider.provider_id}
                              className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full"
                            >
                              {provider.provider_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {providers.rent && (
                      <div>
                        <span className="text-yellow-400 font-medium">Rent: </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {providers.rent.map((provider) => (
                            <span
                              key={provider.provider_id}
                              className="bg-yellow-600 text-white text-xs px-2 py-1 rounded-full"
                            >
                              {provider.provider_name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* External Links */}
              {tmdbData?.id && (
                <div className="flex gap-2">
                  <a
                    href={`https://www.themoviedb.org/movie/${tmdbData.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm transition-colors flex items-center gap-1"
                  >
                    <ExternalLink size={12} />
                    View on TMDB
                  </a>
                  {tmdbData.trailerUrl && (
                    <a
                      href={tmdbData.trailerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-sm transition-colors flex items-center gap-1"
                    >
                      <Play size={12} />
                      Watch Trailer
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Chat channel. Outside the admin block on purpose: the link is for
              everyone, and only the button to open one is admin-only. Not gated
              on movie status either - the point is to gather before it starts. */}
          {channel.status === 'found' && channel.url && (
            <div className="mb-6 p-4 bg-indigo-900/20 border border-indigo-500 rounded-lg">
              <a
                href={channel.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-200 hover:text-white font-bold underline"
              >
                💬 Join the chat channel for this movie
              </a>
              {channel.note && (
                <p className="mt-1 text-sm text-green-300">✅ {channel.note}</p>
              )}
            </div>
          )}

          {isAdmin && channel.status !== 'found' && (
            <div className="mb-6 p-4 bg-indigo-900/20 border border-indigo-500 rounded-lg">
              <button
                onClick={handleCreateChannel}
                disabled={channel.status === 'checking' || channel.status === 'creating'}
                className="bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors w-full"
              >
                {channel.status === 'creating' ? '⏳ Opening channel...' : '💬 Open Chat Channel'}
              </button>
              {channel.status === 'error' && (
                <p className="mt-2 text-sm text-red-300">⚠️ {channel.note}</p>
              )}
            </div>
          )}

          {/* Admin Status Controls */}
          {isAdmin && (
            <div className="mb-6 p-4 bg-red-900/20 border border-red-600 rounded-lg">
              <h4 className="text-red-300 font-bold mb-3">🔧 Admin Controls</h4>
              
              {/* Movie Status Controls */}
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-2">
                  {movieStatusOptions.map(statusOption => (
                    <button
                      key={statusOption.id}
                      onClick={() => onStatusChange && onStatusChange(movieData.id, statusOption.id)}
                      className={`px-3 py-2 rounded text-sm font-bold transition-colors ${
                        movieStatus === statusOption.id
                          ? `${statusOption.color} text-white`
                          : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                      }`}
                    >
                      {statusOption.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Ask for reviews. Not gated on status: an admin pressing this
                  knows the film has been seen, and re-sending is a reminder. */}
              <div className="mb-4">
                <button
                  onClick={handleSendReviewPrompt}
                  disabled={reviewPrompt.busy}
                  className="bg-purple-700 hover:bg-purple-600 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors w-full"
                >
                  {reviewPrompt.busy ? '⏳ Sending...' : '📝 Ask Chat for Reviews'}
                </button>
                {reviewPrompt.note && (
                  <p className={`mt-2 text-sm ${reviewPrompt.failed ? 'text-red-300' : 'text-green-300'}`}>
                    {reviewPrompt.failed ? `⚠️ ${reviewPrompt.note}` : `✅ ${reviewPrompt.note}`}
                  </p>
                )}
              </div>

              {/* Countdown Button */}
              {(movieStatus === 'ready' || movieStatus === 'playing') && (
                <button
                  onClick={() => onStartCountdown && onStartCountdown(movieData.id)}
                  disabled={countdown && countdown.movieId === movieData.id}
                  className="bg-yellow-600 hover:bg-yellow-500 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg font-bold transition-colors w-full"
                >
                  {countdown && countdown.movieId === movieData.id 
                    ? `⏱️ Countdown Active (${countdown.seconds})` 
                    : '⏱️ Start 10s Countdown'
                  }
                </button>
              )}
            </div>
          )}

          {/* Countdown Display - Enhanced and Prominent */}
          {(() => {
            console.log('🔍 ViewerModal countdown check:', {
              hasCountdown: !!countdown,
              countdownMovieId: countdown?.movieId,
              currentMovieId: movieData.id,
              shouldShow: countdown && countdown.movieId === movieData.id
            });
            return countdown && countdown.movieId === movieData.id;
          })() && (
            <div className="mb-6">
              <div className={`text-center p-8 rounded-lg border-4 ${
                countdown.phase === 'goooo' 
                  ? 'border-red-500 bg-red-900 animate-pulse' 
                  : 'border-yellow-500 bg-yellow-900 animate-bounce'
              }`}>
                {countdown.phase === 'goooo' ? (
                  <div className="space-y-2">
                    <div className="text-6xl font-bold text-red-300 animate-pulse">
                      🎬 GOOOO! 🎬
                    </div>
                    <div className="text-xl text-red-200">
                      The movie is starting NOW!
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-6xl font-bold text-red-400 mb-2">
                      {countdown.seconds}
                    </div>
                    {getFreddyMessage(countdown.seconds) && (
                      <div className="text-2xl text-red-300 font-semibold italic">
                        {getFreddyMessage(countdown.seconds)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Join Watching */}
          {canStartWatching() && (
            <div className="mb-6">
              {!isUserWatching(userName) ? (
                <div className="p-4 bg-purple-900/20 border border-purple-600 rounded-lg">
                  <h4 className="text-purple-300 font-bold mb-3">👥 Join Viewing Session</h4>
                  
                  <div className="space-y-3">
                    <NameField
                      name={joinName}
                      onNameChange={setJoinName}
                      profileName={getRememberedName()}
                      action="Joining"
                      accent="purple"
                      onEnter={() => {
                        if (!getSelectedUserName()) return;
                        const movieId = scheduledMovie?.movie?.id || scheduledMovie?.id;
                        onJoinWatching && onJoinWatching(movieId, getSelectedUserName());
                        resetNameInputs();
                      }}
                    />


                    <button
                      onClick={() => {
                        const nameToUse = getSelectedUserName();
                        if (nameToUse && onJoinWatching) {
                          const movieId = scheduledMovie?.movie?.id || scheduledMovie?.id;
                          onJoinWatching(movieId, nameToUse);
                          resetNameInputs();
                        }
                      }}
                      disabled={!getSelectedUserName()}
                      className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 text-white px-4 py-2 rounded font-bold transition-colors w-full"
                    >
                      👁️ I'm Watching
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-green-900/20 border border-green-600 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-green-300 font-bold">✅ You're Watching as {userName}</h4>
                    <button
                      onClick={() => {
                        const movieId = scheduledMovie?.movie?.id || scheduledMovie?.id;
                        onLeaveWatching && onLeaveWatching(movieId, userName);
                      }}
                      className="bg-red-600 hover:bg-red-500 text-white px-3 py-1 rounded text-sm font-bold transition-colors"
                    >
                      Leave
                    </button>
                  </div>
                  
                  {/* Ready Toggle */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="ready-toggle"
                      checked={viewers.find(v => v.name === userName)?.ready || false}
                      onChange={() => {
                        const movieId = scheduledMovie?.movie?.id || scheduledMovie?.id;
                        onToggleReady && onToggleReady(movieId, userName);
                      }}
                      className="rounded border-green-500 text-green-600 focus:ring-green-500"
                    />
                    <label htmlFor="ready-toggle" className="text-green-300 font-bold">
                      Ready to watch
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Review Section for Already Watched Movies */}
          {movieStatus === 'watched' && (
            <div className="mb-6">
              <div className="p-4 bg-yellow-900/20 border border-yellow-600 rounded-lg">
                <h4 className="text-yellow-300 font-bold mb-3">⭐ Movie Reviews</h4>
                
                {/* Average Ratings Display */}
                {reviews.length > 0 && (
                  <div className="mb-4 p-3 bg-gray-900 border border-yellow-500 rounded">
                    {(() => {
                      const averages = getAverageRatings();
                      return (
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-200">Overall:</span>
                            <span className="text-yellow-300 font-bold">({averages.overall}/5)</span>
                            <div className="flex">
                              {averages.overall > 0 ? 
                                Array(Math.round(averages.overall)).fill(0).map((_, i) => (
                                  <span key={i} className="text-yellow-400">⚰️</span>
                                ))
                                : <span className="text-gray-500 text-xs">No ratings</span>
                              }
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-yellow-200">Scary:</span>
                            <span className="text-yellow-300 font-bold">({averages.scariness}/5)</span>
                            <div className="flex">
                              {averages.scariness > 0 ? 
                                Array(Math.round(averages.scariness)).fill(0).map((_, i) => (
                                  <span key={i} className="text-red-400">😱</span>
                                ))
                                : <span className="text-gray-500 text-xs">No ratings</span>
                              }
                            </div>
                          </div>
                          <span className="text-yellow-400">{reviews.length} reviews</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {/* Leave Review Button */}
                <button
                  onClick={() => onOpenReviewModal && onOpenReviewModal(movieData.id)}
                  className="bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded font-bold transition-colors w-full"
                >
                  📝 Leave a Review
                </button>
                
                {/* Individual Reviews */}
                {reviews.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
                    {reviews.map((review, index) => (
                      <div key={index} className="p-3 bg-gray-900 border border-yellow-500 rounded text-sm">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold text-yellow-200">{review.name}</span>
                          <span className="text-yellow-400 text-xs">
                            {new Date(review.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mb-2">
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-300 text-xs">Overall:</span>
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-300 font-bold">{review.overallRating}/5</span>
                              <div className="flex">
                                {review.overallRating > 0 ? 
                                  Array(review.overallRating).fill(0).map((_, i) => (
                                    <span key={i} className="text-yellow-400">⚰️</span>
                                  ))
                                  : <span className="text-gray-500 text-xs">No rating</span>
                                }
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-yellow-300 text-xs">Scary:</span>
                            <div className="flex items-center gap-1">
                              <span className="text-yellow-300 font-bold">{review.scarinessRating}/5</span>
                              <div className="flex">
                                {review.scarinessRating > 0 ? 
                                  Array(review.scarinessRating).fill(0).map((_, i) => (
                                    <span key={i} className="text-red-400">😱</span>
                                  ))
                                  : <span className="text-gray-500 text-xs">No rating</span>
                                }
                              </div>
                            </div>
                          </div>
                        </div>
                        {review.tags && review.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {review.tags.map((tag, tagIndex) => (
                              <span key={tagIndex} className="bg-yellow-700 text-yellow-100 px-2 py-1 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Current Viewers */}
          {viewers.length > 0 && (
            <div className="mb-6">
              <h4 className="text-orange-300 font-bold mb-3">
                👥 Current Viewers ({viewers.length})
              </h4>
              <div className="space-y-2">
                {viewers.map((viewer, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-900 border border-orange-500 rounded">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {viewer.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-orange-100 font-medium">{viewer.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {viewer.ready ? (
                        <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold">✅ Ready</span>
                      ) : (
                        <span className="bg-gray-600 text-gray-300 px-2 py-1 rounded text-xs">⏳ Getting Ready</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Ready Count */}
              <div className="mt-3 text-center text-sm text-orange-400">
                {readyCount} of {viewers.length} viewers ready
              </div>
            </div>
          )}

          {/* Streaming Service */}
          {!isSurveyMode && movieData.preferredStreamingService && (
            <div className="mb-6">
              <h4 className="text-orange-300 font-bold mb-2">📺 Where to Watch</h4>
              {(() => {
                const service = getStreamingServiceById(movieData.preferredStreamingService);
                return service ? (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white ${service.color}`}>
                    <span className="text-lg">{service.icon}</span>
                    <span className="font-bold">{service.name}</span>
                  </div>
                ) : (
                  <div className="text-orange-400 text-sm">Streaming service information not available</div>
                );
              })()}
            </div>
          )}

          {/* Close Button */}
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded-lg font-bold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewerModal;

